class HeroSlider extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
  }

  connectedCallback() {
    // Wait for the next frame to ensure DOM is fully ready
    requestAnimationFrame(() => {
      this.init();
    });
  }

  init() {
    this.container = this.querySelector('.swiper-container');
    if (!this.container) return;

    const autoplayAttr = this.getAttribute('data-autoplay') === 'true';

    const config = {
      slidesPerView: 1,
      loop: true,
      // If using swiper-bundle.min.js, these will work. 
      // If using modules, make sure you've imported EffectFade and Autoplay.
      effect: 'fade',
      fadeEffect: {
        crossFade: true
      },
      autoplay: autoplayAttr ? {
        delay: 5000,
        disableOnInteraction: false,
      } : false,
      speed: 800,
      grabCursor: true
    };

    const mountSwiper = () => {
      if (typeof Swiper !== 'undefined') {
        this.swiper = new Swiper(this.container, config);
        this.dataset.initialized = "true"; // Useful for CSS transitions
      } else {
        console.error('Swiper is not defined. Make sure swiper-bundle.min.js is loaded.');
      }
    };

    // Check if Swiper is already there, otherwise wait for it
    if (typeof Swiper !== 'undefined') {
      mountSwiper();
    } else {
      document.addEventListener('swiper:loaded', mountSwiper, { once: true });
      // Fallback for standard script tags
      window.addEventListener('load', mountSwiper, { once: true });
    }
  }

  // Clean up on section remove (Shopify Editor)
  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy();
    }
  }
}

if (!customElements.get('hero-slider')) {
  customElements.define('hero-slider', HeroSlider);
}

// ------------------------------------------------------- TESTIMONIAL SLIDER -------------------------------------------------------------

  if (!customElements.get('testimonial-slider')) {
    customElements.define('testimonial-slider', class TestimonialSlider extends HTMLElement {
      connectedCallback() {
        if (typeof Swiper === 'undefined') {
          console.warn('Swiper is not defined. Please ensure swiper-bundle.js is loaded.');
          return;
        }
        this.init();
      }

      init() {
        const isRtl = this.getAttribute('data-rtl') === 'true';
        const mediaSwiperEl = this.querySelector('.testimonial-media-column-swiper');
        const contentSwiperEl = this.querySelector('.testimonial-swiper');
        
        let mediaSwiper;

        // Initialize Media Swiper if exists
        if (mediaSwiperEl) {
          mediaSwiper = new Swiper(mediaSwiperEl, {
            slidesPerView: 1,
            spaceBetween: 20,
            loop: true,
            effect: 'fade', // Optional: smooth transition for images
            allowTouchMove: false // Controlled by content slider
          });
        }

        // Initialize Content Swiper
        const contentSwiper = new Swiper(contentSwiperEl, {
          slidesPerView: 1,
          spaceBetween: 20,
          loop: true,
          rtl: isRtl,
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
          },
          navigation: {
            nextEl: this.querySelector('.testimonial-swiper-button-next'),
            prevEl: this.querySelector('.testimonial-swiper-button-prev'),
          }
        });

        // Two-way controller sync
        if (mediaSwiper) {
          contentSwiper.controller.control = mediaSwiper;
          mediaSwiper.controller.control = contentSwiper;
        }
      }
    });
  }



// ============================================ INSTAGRAM CAROUSEL =================================================

  if (!customElements.get('instagram-carousel')) {
    class InstagramCarousel extends HTMLElement {
      constructor() {
        super();
        this.swiper = null;
        this.handleResize = this.init.bind(this);
      }

      connectedCallback() {
        setTimeout(() => this.init(), 10);

        document.addEventListener('shopify:section:load', (e) => {
          if (e.target.contains(this)) this.init();
        });

        window.addEventListener('resize', this.handleResize);
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.handleResize);
        this.destroySwiper();
      }

      init() {
        this.destroySwiper();
        this.initSwiper();
      }

      initSwiper() {
        const container = this.querySelector('.swiper');
        const scrollbar = this.querySelector('.swiper-scrollbar');

        if (!container) return;

        const slidesPerViewMobile  = parseFloat(this.getAttribute('data-slides-per-view-mobile'))  || 1.2;
        const slidesPerViewDesktop = parseFloat(this.getAttribute('data-slides-per-view-desktop')) || 5;
        const spaceBetween         = parseInt(this.getAttribute('data-space-between'))             || 12;

        this.swiper = new Swiper(container, {
          slidesPerView: slidesPerViewMobile,
          spaceBetween,
          grabCursor: true,
          scrollbar: {
            el: scrollbar,
            draggable: true,
            hide: false,
          },
          breakpoints: {
            750: {
              slidesPerView: 3,
              spaceBetween: 16,
            },
            1200: {
              slidesPerView: slidesPerViewDesktop,
              spaceBetween: 16,
            },
          },
        });
      }

      destroySwiper() {
        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }
      }
    }

    customElements.define('instagram-carousel', InstagramCarousel);
  }


  // ==================================== BANNER CAROUSEL ===============================================

if (!customElements.get('banner-carousel')) {
  customElements.define('banner-carousel', class BannerCarousel extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      // Check if Swiper exists, otherwise wait or load it
      if (typeof Swiper !== 'undefined') {
        this.init();
      } else {
        document.addEventListener('swiper:loaded', () => this.init());
      }
    }

    init() {
      this.slider = new Swiper(this, {
        slicePerView: 1,
        loop: true,
        effect: 'fade',
        touchStartPreventDefault: false,
        touchMoveStopPropagation: false,
        freeMode: true,
        freeModeMomentum: true,
        freeModeMomentumRatio: 1,
        freeModeMomentumVelocityRatio: 1,
        freeModeSticky: false,
        freeModeMinimumVelocity: 0.02,
        nested: true,
        setWrapperSize: true,
        allowTouchMove: function (swiper, event) {
          const targetElement = event.target;
          const isTargetScrollableContainer = targetElement.closest('.collection-tabs-container');
          const isTargetScrollableNav = targetElement.closest('nav.collection-tabs-navigation');

          if (isTargetScrollableContainer || isTargetScrollableNav) {
            return false;
          }
          return true;
        },
        fadeEffect: { crossFade: true },
        pagination: { el: '.swiper-pagination', clickable: true },
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        autoplay: false
      });
    }
  });
}


// ================================== Urgency Countdown =====================================

  class UrgencyTimer extends HTMLElement {
    constructor() {
      super();
      this.timerContainer = null;
      this.interval = null;
      this.targetTime = null;
    }

    connectedCallback() {
      this.timerContainer = this.querySelector('#urgency_timer');

      const futureDateAttr = this.getAttribute('data-future-date');

      if (!futureDateAttr) return;

      const targetDate = new Date(futureDateAttr);

      if (isNaN(targetDate.getTime())) return;

      this.targetTime = targetDate.getTime();

      this.startTimer();
    }

    startTimer() {
      this.updateTimer();

      this.interval = setInterval(() => {
        const now = Date.now();
        const distance = this.targetTime - now;

        if (distance <= 0) {
          this.style.display = 'none';
          clearInterval(this.interval);
        } else {
          this.classList.remove('hide');
          this.updateTimer();
        }
      }, 1000);
    }

    updateTimer() {
      const now = Date.now();
      const distance = this.targetTime - now;

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      const display = {
        days: String(days).padStart(2, '0'),
        hours: String(hours).padStart(2, '0'),
        minutes: String(minutes).padStart(2, '0'),
        seconds: String(seconds).padStart(2, '0'),
      };

      if (this.timerContainer) {
        this.timerContainer.innerHTML = `
    <div class="time-block time-block-days">
      <span class="time-value">${display.days}</span>
      <span class="time-unit">${display.days > 1 ? 'D' : 'Day'}</span>
    </div>
    <span class="seperator">:</span>
    <div class="time-block time-block-hours">
      <span class="time-value">${display.hours}</span>
      <span class="time-unit">${display.hours > 1 ? 'H' : 'Hour'}</span>
    </div>
     <span class="seperator">:</span>
    <div class="time-block time-block-minute">
      <span class="time-value">${display.minutes}</span>
      <span class="time-unit">${display.minutes > 1 ? 'M' : 'Min'}</span>
    </div>
     <span class="seperator time-block-second">:</span>
    <div class="time-block time-block-second">
      <span class="time-value">${display.seconds}</span>
      <span class="time-unit">${display.seconds > 1 ? 'S' : 'Sec'}</span>
    </div>
  `;
      }
    }
  }

  customElements.define('custom-deliver-order', UrgencyTimer);


//   ============================ PDP TABS ============================================

document.addEventListener('click', (e) => {
  const trigger = e.target.closest('.pdp-tabs__trigger');
  if (!trigger) return;
  activateTab(trigger);
});

document.addEventListener('keydown', (e) => {
  if (e.key !== 'Enter' && e.key !== ' ') return;
  const trigger = e.target.closest('.pdp-tabs__trigger');
  if (!trigger) return;
  e.preventDefault();
  activateTab(trigger);
});

function activateTab(trigger) {
  const tabs = trigger.closest('.pdp-tabs');
  const index = trigger.dataset.tab;

  tabs.querySelectorAll('.pdp-tabs__trigger').forEach(t => {
    t.classList.remove('is-active');
    t.setAttribute('aria-selected', 'false');
    t.setAttribute('tabindex', '-1');
  });
  trigger.classList.add('is-active');
  trigger.setAttribute('aria-selected', 'true');
  trigger.setAttribute('tabindex', '0');

  tabs.querySelectorAll('.pdp-tabs__panel').forEach(p => {
    p.classList.remove('is-active');
    p.hidden = true;
  });
  const activePanel = tabs.querySelector(`.pdp-tabs__panel[data-tab="${index}"]`);
  if (activePanel) {
    activePanel.classList.add('is-active');
    activePanel.hidden = false;
  }
}


// ===================================================== Localization SElector ==================================================
  if (!customElements.get('localization-selector')) {
    class LocalizationSelector extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.input = this.querySelector('input[name="country_code"]');
        this.items = this.querySelectorAll('dropdown-menu-item');

        this.items.forEach((item) => {
          item.addEventListener('click', this.onItemClick.bind(this));
          item.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this.onItemClick(e);
            }
          });
        });
      }

      onItemClick(event) {
        event.preventDefault();
        const item = event.currentTarget;
        const countryCode = item.dataset.value;

        if (this.input) {
          this.input.value = countryCode;
          if (this.form) this.form.submit();
        }
      }
    }
    customElements.define('localization-selector', LocalizationSelector);
  }

//   ======================================= ARTICLE SLIDER ===================================================

if (!customElements.get('article-product-slider')) {
  customElements.define('article-product-slider', class ArticleProductSlider extends HTMLElement {
    connectedCallback() {
      if (typeof Swiper === 'undefined') return;
      this.init();
    }

    init() {
      const desktop = parseFloat(this.getAttribute('data-desktop')) || 3;
      const tablet = parseFloat(this.getAttribute('data-tablet')) || 2;
      const mobile = parseFloat(this.getAttribute('data-mobile')) || 1.2;

      new Swiper(this.querySelector('.article-product-swiper'), {
        slidesPerView: mobile,
        spaceBetween: 16,
        scrollbar: {
          el: this.querySelector('.swiper-scrollbar'),
          draggable: true,
          dragSize: 'auto'
        },
        breakpoints: {
          768: {
            slidesPerView: tablet,
          },
          1024: {
            slidesPerView: desktop,
            spaceBetween: 48
          }
        }
      });
    }
  });
}